import { useMemo, type VoidFunctionComponent } from 'react';
import Helmet from 'react-helmet';
import { useDispatch } from 'react-redux';

import { window } from '../../../globals';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { trimString } from '../../../utils';
import CallToAction from '../../CallToAction';
import PageHeadingBreadcrumbs from '../../generic/PageHeadingBreadcrumbs';
import TeamSelectionMenu from '../../shared/teams/TeamSelectionMenu';
import type { Organization } from '../../../types';

/**
 * GlobalIssuesCallToAction is essentially a clone and replacement of IssuesDashboardNav,
 * but connected to the issuesv2 store instead.
 * Dispatches the category fetch action when it mounts, then displays the counts
 * for each category next to the nav item.
 */
const GlobalIssuesCallToAction: VoidFunctionComponent = () => {
  const dispatch = useDispatch();
  const crumbs = useMemo(() => [
    {
      text: trimString((window?.user?.organization as Organization)?.title ?? 'Organization', 20),
      to: '/projects',
    },
    {
      text: 'Issues',
    },
    {
      content: (
        <TeamSelectionMenu
          onChange={() => {
            dispatch(IssuesV2Store.actions.clearSelection());
            dispatch(IssuesV2Store.actions.preFetchLoading());
            dispatch(IssuesV2Store.actions.fetchIssues());
            dispatch(IssuesV2Store.actions.fetchStatusSummary());
          }}
        />
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Even though window.user.organization should not change, we include it in case.
  ], [dispatch, window?.user?.organization]);

  return (
    <CallToAction shouldHideBottomBorder>
      <Helmet title="Issues" />
      <PageHeadingBreadcrumbs crumbs={crumbs} />
    </CallToAction>
  );
};

export default GlobalIssuesCallToAction;
